/* === AgTabBar -- Anagogia Design System (C11) === */
import { Children } from 'react';
import styles from './AgTabBar.module.css';

interface AgTabBarItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  /** Destination annoncée mais pas encore livrée : atténuée, libellé suffixé « (bientôt) » pour les lecteurs d'écran */
  soon?: boolean;
  onClick: () => void;
}

/**
 * Onglet de la barre basse : icône + libellé toujours visible (pas de tooltip au tactile).
 * @category Navigation
 */
export function AgTabBarItem({ icon, label, active = false, soon = false, onClick }: AgTabBarItemProps) {
  return (
    <button
      type="button"
      className={[styles.tab, active ? styles.active : '', soon ? styles.soon : ''].filter(Boolean).join(' ')}
      aria-current={active ? 'page' : undefined}
      aria-label={soon ? `${label} (bientôt)` : undefined}
      onClick={onClick}
    >
      <span className={styles.icon} aria-hidden="true">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

interface AgTabBarProps {
  children: React.ReactNode;
  /** Emplacement central (typiquement un AgFab) qui déborde de 18 px au-dessus de la barre */
  center?: React.ReactNode;
  className?: string;
}

/**
 * Barre d'onglets basse du shell mobile : quatre destinations et un emplacement
 * central. Fixée en bas, strate `--z-index-tab-bar`, padding `--ag-safe-bottom`.
 * @category Navigation
 */
export function AgTabBar({ children, center, className }: AgTabBarProps) {
  const items = Children.toArray(children);
  const half = Math.ceil(items.length / 2);
  return (
    <nav className={[styles.bar, className].filter(Boolean).join(' ')} aria-label="Navigation principale">
      {items.slice(0, half)}
      {center && (
        <div className={styles.center}>
          <div className={styles.centerRing}>{center}</div>
        </div>
      )}
      {items.slice(half)}
    </nav>
  );
}

export type { AgTabBarProps, AgTabBarItemProps };
export default AgTabBar;
